import React, { useEffect, useState } from "react";
import AdminLayout from "./AdminLayout.js";
import { useNavigate } from "react-router-dom";

const EVENT_CONFIG = {
  site_visit:       { label: "Site Visit",        color: "#6366f1", bg: "#eef2ff", icon: "◎" },
  product_view:     { label: "Product Viewed",    color: "#0ea5e9", bg: "#e0f2fe", icon: "◈" },
  add_to_cart:      { label: "Added to Cart",     color: "#f59e0b", bg: "#fef3c7", icon: "◫" },
  checkout_started: { label: "Checkout Started",  color: "#f97316", bg: "#fff7ed", icon: "◷" },
  order_placed:     { label: "Order Placed",      color: "#10b981", bg: "#d1fae5", icon: "✓" }
};

function timeAgo(date) {
  const diff = Math.floor((Date.now() - new Date(date)) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(date).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export default function DashboardScreen() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [events, setEvents] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [revenue, setRevenue] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAll();
const interval = setInterval(fetchAll, 15000);
    return () => clearInterval(interval);
  }, []);

const fetchAll = async () => {
    try {
      const token = localStorage.getItem("token");
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
      const [summaryRes, eventsRes, topRes, ordersRes] = await Promise.all([
        fetch("https://eleven11-i6v4.onrender.com/api/analytics/summary", { headers: authHeaders }),
        fetch("https://eleven11-i6v4.onrender.com/api/analytics/events?limit=20", { headers: authHeaders }),
        fetch("https://eleven11-i6v4.onrender.com/api/analytics/top-products", { headers: authHeaders }),
        fetch("https://eleven11-i6v4.onrender.com/api/orders", { headers: authHeaders })
      ]);


      const summaryData = await summaryRes.json();
      const eventsData = await eventsRes.json();
      const topData = await topRes.json();
      const ordersData = await ordersRes.json();

      setSummary(summaryData);
      setEvents(eventsData.events || []);
      setTopProducts(topData.topProducts || []);

      const allOrders = ordersData.orders || [];
      setOrders(allOrders.slice(0, 5));
      setRevenue(allOrders.reduce((sum, o) => sum + Number(o.total || 0), 0));

    } catch (err) {
      console.error("Dashboard fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

const fetchEvents = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch("https://eleven11-i6v4.onrender.com/api/analytics/events?limit=20", {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      
      const data = await res.json();
      setEvents(data.events || []);
    } catch {}
  };

  const METRIC_CARDS = summary ? [
    { label: "Today's Visitors", value: summary.todayVisits, sub: `${summary.totalVisits} total`, color: "#6366f1" },
    { label: "Product Views", value: summary.totalProductViews, sub: "All time", color: "#0ea5e9" },
    { label: "Cart Adds", value: summary.totalAddToCart, sub: "All time", color: "#f59e0b" },
    { label: "Checkouts Started", value: summary.totalCheckouts, sub: "All time", color: "#f97316" },
    { label: "Orders Placed", value: summary.totalOrders, sub: `${summary.todayOrders} today`, color: "#10b981" },
    { label: "Total Revenue", value: `$${revenue.toLocaleString()}`, sub: "All orders", color: "#8b5cf6" }
  ] : [];

  const FULFILLMENT_COLORS = {
    order_placed: { bg: "#EEF2FF", color: "#4361C2" },
    payment_confirmed: { bg: "#EEF2FF", color: "#4361C2" },
    packaged: { bg: "#FFF7E6", color: "#B45309" },
    shipped: { bg: "#E6F0FF", color: "#1D4ED8" },
    out_for_delivery: { bg: "#FFF0F0", color: "#DC2626" },
    delivered: { bg: "#EDFDF4", color: "#15803D" }
  };

  const FULFILLMENT_LABELS = {
    order_placed: "Order Placed",
    payment_confirmed: "Payment Confirmed",
    packaged: "Packaged",
    shipped: "Shipped",
    out_for_delivery: "Out for Delivery",
    delivered: "Delivered"
  };

  if (loading) {
    return (
      <AdminLayout>
        <div style={styles.loadingWrap}>Loading dashboard...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>

      {/* Header */}
      <div style={styles.header}>
        <div>
          <p style={styles.kicker}>ELEVEN CONTROL CENTER</p>
          <h1 style={styles.title}>Dashboard</h1>
        </div>
        <div style={styles.headerRight}>
          <div style={styles.liveIndicator}>
            <div style={styles.liveDot} />
            Live
          </div>
          <div style={styles.dateText}>
            {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
          </div>
        </div>
      </div>

      {/* Metric Cards */}
      <div style={styles.metricsGrid}>
        {METRIC_CARDS.map((card) => (
          <div key={card.label} style={styles.metricCard}>
            <div style={{ ...styles.metricAccent, background: card.color }} />
            <p style={styles.metricLabel}>{card.label}</p>
            <h2 style={{ ...styles.metricValue, color: card.color }}>{card.value}</h2>
            <p style={styles.metricSub}>{card.sub}</p>
          </div>
        ))}
      </div>

      {/* Two Column Layout */}
      <div style={styles.twoCol}>

        {/* Activity Feed */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>Live Activity Feed</h3>
            <button style={styles.refreshBtn} onClick={fetchEvents}>↻ Refresh</button>
          </div>

          <div style={styles.feedList}>
            {events.length === 0 && (
              <p style={styles.emptyText}>No activity yet — browse the store to start tracking.</p>
            )}
            {events.map((event, i) => {
              const config = EVENT_CONFIG[event.eventType] || { label: event.eventType, color: "#888", bg: "#f5f5f5", icon: "•" };
              return (
                <div key={event._id || i} style={styles.feedItem}>
                  <div style={{ ...styles.feedBadge, background: config.bg, color: config.color }}>
                    <span style={styles.feedIcon}>{config.icon}</span>
                    {config.label}
                  </div>

<div style={{ ...styles.feedMeta, flexDirection: "column", alignItems: "flex-start", gap: 4 }}>
  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
    {event.userName && (
      <span style={styles.feedUser}>{event.userName}</span>
    )}
    {event.listingTitle && (
      <span style={styles.feedTitle}>{event.listingTitle}</span>
    )}
    {event.metadata?.orderNumber && (
      <span style={styles.feedTitle}>{event.metadata.orderNumber}</span>
    )}
    {event.metadata?.total && (
      <span style={styles.feedAmount}>${Number(event.metadata.total).toFixed(2)}</span>
    )}
  </div>
  {event.eventType === "checkout_started" && event.metadata?.items?.length > 0 && (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {event.metadata.items.map((item, idx) => (
        <div key={idx} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#6b7280" }}>
          <span style={{ fontWeight: 700, color: "#f97316" }}>×{item.qty}</span>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 240 }}>{item.title}</span>
          {item.condition && (
            <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 999, background: item.condition === "new" ? "#e0f2fe" : "#f3f4f6", color: item.condition === "new" ? "#0369a1" : "#6b7280" }}>
              {item.condition === "new" ? "New" : "Used"}
            </span>
          )}
          <span style={{ fontWeight: 600, color: "#374151", flexShrink: 0 }}>${Number(item.price).toFixed(2)}</span>
        </div>
      ))}
    </div>
  )}
</div>


                  <span style={styles.feedTime}>{timeAgo(event.timestamp)}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right Column */}
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

          {/* Top Products */}
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <h3 style={styles.cardTitle}>Top Products</h3>
            </div>
            {topProducts.length === 0 && (
              <p style={styles.emptyText}>No product views yet.</p>
            )}
            {topProducts.map((p, i) => (
              <div key={p._id} style={styles.topProductRow}>
                <div style={styles.topProductRank}>#{i + 1}</div>
                <div style={styles.topProductInfo}>
                  <p style={styles.topProductTitle}>{p.title || p._id}</p>
                  <p style={styles.topProductViews}>{p.views} view{p.views !== 1 ? "s" : ""}</p>
                </div>
                <div style={styles.topProductBar}>
                  <div style={{
                    ...styles.topProductBarFill,
                    width: `${Math.min(100, (p.views / (topProducts[0]?.views || 1)) * 100)}%`
                  }} />
                </div>
              </div>
            ))}
          </div>

          {/* Recent Orders */}
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <h3 style={styles.cardTitle}>Recent Orders</h3>
              <button
                style={styles.refreshBtn}
                onClick={() => navigate("/admin/orders")}
              >
                View All →
              </button>
            </div>
            {orders.length === 0 && (
              <p style={styles.emptyText}>No orders yet.</p>
            )}
            {orders.map((order) => {
              const stage = order.fulfillmentStatus || "order_placed";
              const badge = FULFILLMENT_COLORS[stage] || FULFILLMENT_COLORS.order_placed;
              const label = FULFILLMENT_LABELS[stage] || stage;
              return (
                <div
                  key={order._id}
                  style={styles.orderRow}
                  onClick={() => navigate(`/admin/orders/${order._id}`)}
                >
                  <div>
                    <p style={styles.orderNumber}>{order.orderNumber}</p>
                    <p style={styles.orderCustomer}>
                      {order.customer?.firstName} {order.customer?.lastName}
                    </p>
                  </div>
                  <div style={styles.orderRight}>
                    <span style={{ ...styles.statusBadge, background: badge.bg, color: badge.color }}>
                      {label}
                    </span>
                    <p style={styles.orderTotal}>${Number(order.total || 0).toFixed(2)}</p>
                  </div>
                </div>
              );
            })}
          </div>

        </div>

      </div>

    </AdminLayout>
  );
}

const styles = {

  loadingWrap: {
    textAlign: "center",
    marginTop: 120,
    fontSize: 16,
    color: "#8a8e98"
  },

  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: 32
  },

  kicker: {
    margin: 0,
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 2,
    color: "#9ca3af",
    textTransform: "uppercase"
  },

  title: {
    margin: "6px 0 0",
    fontSize: 36,
    fontWeight: 800,
    letterSpacing: -1,
    color: "#0f0f17"
  },

  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    paddingTop: 8
  },

  liveIndicator: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 13,
    fontWeight: 700,
    color: "#10b981",
    background: "#d1fae5",
    padding: "6px 12px",
    borderRadius: 999
  },

  liveDot: {
    width: 7,
    height: 7,
    borderRadius: "50%",
    background: "#10b981",
    animation: "pulse 2s infinite"
  },

  dateText: {
    fontSize: 13,
    color: "#9ca3af",
    fontWeight: 500
  },

  metricsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(6, 1fr)",
    gap: 16,
    marginBottom: 28
  },

  metricCard: {
    background: "#ffffff",
    borderRadius: 18,
    padding: "20px 18px",
    position: "relative",
    overflow: "hidden",
    boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 8px 24px rgba(0,0,0,0.04)"
  },

  metricAccent: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 3,
    borderRadius: "18px 18px 0 0"
  },

  metricLabel: {
    margin: "8px 0 10px",
    fontSize: 11,
    fontWeight: 700,
    color: "#9ca3af",
    textTransform: "uppercase",
    letterSpacing: 0.8
  },

  metricValue: {
    margin: 0,
    fontSize: 28,
    fontWeight: 900,
    letterSpacing: -1
  },

  metricSub: {
    margin: "6px 0 0",
    fontSize: 12,
    color: "#9ca3af"
  },

  twoCol: {
    display: "grid",
    gridTemplateColumns: "1fr 380px",
    gap: 20
  },

  card: {
    background: "#ffffff",
    borderRadius: 18,
    padding: "24px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 8px 24px rgba(0,0,0,0.04)"
  },

  cardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 20
  },

  cardTitle: {
    margin: 0,
    fontSize: 15,
    fontWeight: 800,
    color: "#0f0f17"
  },

  refreshBtn: {
    border: "none",
    background: "transparent",
    fontSize: 13,
    fontWeight: 600,
    color: "#6366f1",
    cursor: "pointer",
    padding: 0
  },

  emptyText: {
    color: "#9ca3af",
    fontSize: 14,
    textAlign: "center",
    padding: "20px 0"
  },

  feedList: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
    maxHeight: 480,
    overflowY: "auto"
  },

feedItem: {
  display: "flex",
  alignItems: "flex-start",
  gap: 12,
  padding: "10px 0",
  borderBottom: "1px solid #f3f4f6"
},

  feedBadge: {
    display: "flex",
    alignItems: "center",
    gap: 5,
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700,
    whiteSpace: "nowrap",
    flexShrink: 0
  },

  feedIcon: {
    fontSize: 13
  },

  feedMeta: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    gap: 8,
    overflow: "hidden"
  },

  feedTitle: {
    fontSize: 13,
    color: "#374151",
    fontWeight: 500,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
  },


feedUser: {
  fontSize: 12,
  fontWeight: 700,
  color: "#6366f1",
  background: "#eef2ff",
  padding: "2px 7px",
  borderRadius: 999,
  flexShrink: 0,
  whiteSpace: "nowrap"
},


  feedAmount: {
    fontSize: 13,
    fontWeight: 700,
    color: "#10b981",
    flexShrink: 0
  },

  feedTime: {
    fontSize: 12,
    color: "#9ca3af",
    flexShrink: 0
  },

  topProductRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "10px 0",
    borderBottom: "1px solid #f3f4f6"
  },

  topProductRank: {
    fontSize: 13,
    fontWeight: 800,
    color: "#d1d5db",
    width: 24,
    flexShrink: 0
  },

  topProductInfo: {
    flex: 1,
    overflow: "hidden"
  },

  topProductTitle: {
    margin: 0,
    fontSize: 13,
    fontWeight: 600,
    color: "#374151",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
  },

  topProductViews: {
    margin: "2px 0 0",
    fontSize: 12,
    color: "#9ca3af"
  },

  topProductBar: {
    width: 60,
    height: 4,
    borderRadius: 999,
    background: "#f3f4f6",
    overflow: "hidden",
    flexShrink: 0
  },

  topProductBarFill: {
    height: "100%",
    borderRadius: 999,
    background: "#6366f1",
    transition: "width 0.3s ease"
  },

  orderRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 0",
    borderBottom: "1px solid #f3f4f6",
    cursor: "pointer"
  },

  orderNumber: {
    margin: 0,
    fontSize: 13,
    fontWeight: 700,
    color: "#0f0f17"
  },






  
  orderCustomer: {
    margin: "3px 0 0",
    fontSize: 12,
    color: "#9ca3af"
  },

  orderRight: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: 4
  },

  statusBadge: {
    padding: "3px 10px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700
  },

  orderTotal: {
    margin: 0,
    fontSize: 14,
    fontWeight: 800,
    color: "#0f0f17"
  }
};